// CommandPalette —— ⌘K 全局命令面板（F60）
// - Portal + fixed overlay + 输入框 + 分组结果（别名 / 邮件）
// - ↑↓ 导航 + Enter 跳转 + ESC 关；跳转后关闭并清空 query
// - q < 2 显示 hint"请输入至少 2 字"
// - 别名跳 /app/aliases?highlight={id}；邮件跳 /app/inbox/{id}

import { AtSign, Command as CommandIcon, Loader2, Mail } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { cn } from '../lib/cn.js';
import { useDebounced, useSearch } from '../lib/search.js';
import { formatRelative } from '../lib/time.js';

interface Props {
  open: boolean;
  onClose: () => void;
}

type Item =
  | { kind: 'alias'; id: number; label: string; sub: string }
  | { kind: 'message'; id: number; label: string; sub: string; when: string };

export function CommandPalette({ open, onClose }: Props) {
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const debounced = useDebounced(q, 200);
  const searchQ = useSearch(debounced, open);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // 打开时 focus 输入
  useEffect(() => {
    if (open) {
      // 让 Portal 挂上后再 focus
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    setQ('');
    setCursor(0);
    return undefined;
  }, [open]);

  const trimmed = debounced.trim();
  const showHint = trimmed.length > 0 && trimmed.length < 2;
  const hasQuery = trimmed.length >= 2;
  // 旧结果可以暂时展示，但防抖中或占位数据不能作为新输入的跳转目标。
  const resultsCurrent = q.trim() === trimmed && !searchQ.isPlaceholderData;

  const items: Item[] = useMemo(() => {
    if (!hasQuery) return [];
    const d = searchQ.data;
    if (!d) return [];
    const out: Item[] = [];
    for (const a of d.aliases) {
      out.push({
        kind: 'alias',
        id: a.id,
        label: a.address,
        sub: a.source === 'gmail_plus' ? 'Gmail Plus' : (a.domain ?? 'CF 域名'),
      });
    }
    for (const m of d.messages) {
      out.push({
        kind: 'message',
        id: m.id,
        label: m.subject || '(无主题)',
        sub: `${m.fromAddress}${m.snippet ? ` · ${m.snippet}` : ''}`,
        when: formatRelative(m.receivedAt),
      });
    }
    return out;
  }, [hasQuery, searchQ.data]);

  // 命中列表变化时 clamp cursor
  useEffect(() => {
    if (cursor > items.length - 1) setCursor(Math.max(0, items.length - 1));
  }, [items.length, cursor]);

  const jump = (item: Item): void => {
    if (!resultsCurrent) return;
    if (item.kind === 'alias') {
      void navigate(`/app/aliases?highlight=${String(item.id)}`);
    } else {
      void navigate(`/app/inbox/${String(item.id)}`);
    }
    onClose();
  };

  const onKey = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (items.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => (c + 1) % items.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => (c - 1 + items.length) % items.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[cursor];
      if (item) jump(item);
    }
  };

  if (!open) return null;

  // 分组渲染：aliases 段 + messages 段
  const aliasCount = items.filter((i) => i.kind === 'alias').length;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-slate-950/40 p-4 pt-4 backdrop-blur-sm sm:pt-[10vh]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={onKey}
      role="dialog"
      aria-modal="true"
      aria-label="全局搜索"
    >
      <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2.5 dark:border-slate-800">
          <CommandIcon className="h-4 w-4 text-slate-400" />
          <input
            ref={inputRef}
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setCursor(0);
            }}
            placeholder="搜索别名或邮件（至少 2 个字）..."
            className="h-8 flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
          />
          {q.trim().length >= 2 && (!resultsCurrent || searchQ.isFetching) ? (
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          ) : null}
          <kbd className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 whitespace-nowrap">
            ESC
          </kbd>
        </div>

        <div className="max-h-[50dvh] overflow-y-auto sm:max-h-[60vh]">
          {showHint ? (
            <div className="px-4 py-6 text-center text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
              请输入至少 2 字
            </div>
          ) : !hasQuery ? (
            <div className="px-4 py-6 text-center text-xs text-slate-500 dark:text-slate-400">
              输入关键词搜索别名与邮件
            </div>
          ) : searchQ.isLoading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-xs text-slate-500 dark:text-slate-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              搜索中…
            </div>
          ) : searchQ.isError ? (
            <div className="px-4 py-6 text-center text-xs text-red-500">
              搜索失败：{searchQ.error?.message ?? '未知错误'}
            </div>
          ) : items.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-slate-500 dark:text-slate-400">
              未找到相关别名或邮件
            </div>
          ) : (
            <ul className="p-1">
              {aliasCount > 0 ? (
                <li className="px-2 pt-1.5 pb-1 text-[10px] font-medium uppercase tracking-wide text-slate-400 whitespace-nowrap">
                  别名（{aliasCount}）
                </li>
              ) : null}
              {items.map((item, idx) => {
                const isFirstMessage =
                  item.kind === 'message' &&
                  (idx === 0 || items[idx - 1]?.kind !== 'message');
                return (
                  <li key={`${item.kind}-${String(item.id)}`}>
                    {isFirstMessage ? (
                      <div className="px-2 pt-2 pb-1 text-[10px] font-medium uppercase tracking-wide text-slate-400 whitespace-nowrap">
                        邮件（{items.length - aliasCount}）
                      </div>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => jump(item)}
                      disabled={!resultsCurrent}
                      onMouseEnter={() => setCursor(idx)}
                      className={cn(
                        'flex w-full items-start gap-2.5 rounded-md px-2 py-2 text-left transition-colors',
                        idx === cursor
                          ? 'bg-brand-50 dark:bg-brand-500/10'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800/60',
                      )}
                    >
                      {item.kind === 'alias' ? (
                        <AtSign className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                      ) : (
                        <Mail className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                          {item.label}
                        </div>
                        <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                          {item.sub}
                        </div>
                      </div>
                      {item.kind === 'message' ? (
                        <span className="shrink-0 text-[10px] text-slate-400 whitespace-nowrap">
                          {item.when}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-3 py-2 text-[10px] text-slate-400 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <span>
              <Key>↑</Key>
              <Key>↓</Key> 导航
            </span>
            <span>
              <Key>↵</Key> 打开
            </span>
            <span>
              <Key>ESC</Key> 关闭
            </span>
          </div>
          <span className="hidden whitespace-nowrap sm:inline">AliasHub 全局搜索</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="mx-0.5 rounded border border-slate-200 bg-white px-1 py-0.5 text-[9px] font-medium text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
      {children}
    </kbd>
  );
}
